import * as React from "react";
import * as moment from "moment-timezone";

import {
  useCutoffTimeForWeekDay,
  useDaysToCutoff
} from "src/core/orders/hooks/validation";
import { IProduct } from "src/core/products/api/products.api";
import { getWeekDayFromDate } from "src/core/orders/utils/order-deliveries";
import useDrop from "src/core/drops/hooks/useDrop";
import {
  hourMinuteTimeSplit,
  toLocalTime
} from "src/library/utils/time-helpers/time-helpers";
import { Text } from "src/library/components/Text";
import { useLeadTimeOverrideCutoff } from "src/core/orders/hooks/validation/useLeadTimeOverrideCutoff";

import useMinimumLeadTime from "./useMinimumLeadTime";
import useTimezone from "./time-helpers/useTimeZone";

/**
   * Returns an array of dates with warning messages if needed
   */
export const useWarningDeliveryDates = (): typeof isWarning => {
  const getCuttOffTimeForWeekDay = useCutoffTimeForWeekDay();
  const daysToCutOff = useDaysToCutoff();
  const [ { drop } ] = useDrop();
  const { minimumLeadTime } = useMinimumLeadTime();
  const timezone = useTimezone();
  const leadTimeOverrideCutoff = useLeadTimeOverrideCutoff();

  const isWarning = React.useCallback((date: Date, product?: IProduct) => {
    const deliveryDate = moment.utc(date);

    if (drop && drop.groupedSettings.delivery_week_leadtimes && drop.groupedSettings.delivery_cutoff) {
      const leadtime = (product && product.leadTimeGroup ? product.leadTimeGroup : minimumLeadTime);

      if (!leadtime) {
        return { isWarning: false };
      }

      const leadTimeOverrideCutoffTime = leadTimeOverrideCutoff(date);

      if (leadTimeOverrideCutoffTime) {
        const cutoffIsToday = moment.utc().isSame(moment.utc(leadTimeOverrideCutoffTime), "day");

        if (cutoffIsToday) {
          const split = moment.utc(leadTimeOverrideCutoffTime).format("HH:mm");

          return {
            isWarning: true,
            component: (
              <Text className="warning">
                {`Place your order before ${split}`}
              </Text>
            )
          };
        }
      }

      const daysToCutoffTotal = daysToCutOff(date, leadtime);

      if (typeof daysToCutoffTotal === "number" && daysToCutoffTotal < 1) {
        const weekDay = getWeekDayFromDate(deliveryDate);
        const cutoffTime = getCuttOffTimeForWeekDay(leadtime, weekDay);

        if (cutoffTime) {
          const { hours, minutes } = hourMinuteTimeSplit(cutoffTime);

          if (
            moment.utc().isAfter(
              moment
                .tz(timezone)
                .hours(hours)
                .minutes(minutes)
            )
          ) {
            deliveryDate.add(1, "day");
          }
        }

        return {
          isWarning: true,
          component: (
            <Text className="warning">
              {`Place your order before ${cutoffTime ? toLocalTime(cutoffTime, timezone) : ""}`}

            </Text>
          )
        };
      }
    }

    return { isWarning: false };
  }, [
    drop,
    minimumLeadTime,
    daysToCutOff,
    getCuttOffTimeForWeekDay,
    timezone,
    leadTimeOverrideCutoff
  ]);

  return isWarning;
};
